import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildVariantUrl } from '../utils/photoCache';
import {
  LuShare2,
  LuFolder,
  LuBriefcase,
  LuImage,
  LuEye,
  LuClock,
  LuCalendar,
  LuLock,
  LuCopy,
  LuCheck,
  LuExternalLink,
  LuChartBar,
  LuX,
  LuSearch,
  LuEllipsisVertical,
  LuPencil,
  LuTrash2,
  LuRotateCcw,
  LuSlash,
  LuUsers,
  LuBookmark,
  LuGlobe,
  LuInfo,
  LuDownload,
  LuPlus,
  LuChevronDown,
  LuMail,
  LuLink
} from 'react-icons/lu';

const STATUS_CONFIG = {
  active:   { label: 'Active',   cls: 'st-active',   barCls: 'bar-active',   Icon: LuEye },
  expiring: { label: 'Expiring', cls: 'st-expiring',  barCls: 'bar-expiring',  Icon: LuClock },
  revoked:  { label: 'Revoked',  cls: 'st-revoked',   barCls: 'bar-revoked',   Icon: LuSlash },
  expired:  { label: 'Expired',  cls: 'st-expired',   barCls: 'bar-expired',   Icon: LuClock },
};

const STATUS_FILTERS = [
  { k: 'all',      label: 'All',      dot: null },
  { k: 'active',   label: 'Active',   dot: 'var(--color-success, #2EA06B)' },
  { k: 'expiring', label: 'Expiring', dot: 'var(--color-warning, #E89B2E)' },
  { k: 'revoked',  label: 'Revoked',  dot: 'var(--color-danger, #D94B4B)' },
  { k: 'expired',  label: 'Expired',  dot: 'var(--color-gray-300, #B8C3D1)' },
];

const SOURCE_ICONS = {
  direct: LuBookmark,
  social: LuGlobe,
  email: LuMail,
  search: LuSearch,
  other: LuGlobe,
  unknown: LuLink,
};

const SOURCE_LABELS = {
  direct: 'Direct / Bookmarks',
  social: 'Social Media',
  email: 'Email',
  search: 'Search Engines',
  other: 'Other Websites',
  unknown: 'Unknown',
};

function getShareStatus(share) {
  const isRevoked = share.revoked || share.revoked_at;
  const isExpired = share.expires_at && new Date(share.expires_at) < new Date();
  if (isRevoked) return 'revoked';
  if (isExpired) return 'expired';
  if (share.expires_at) {
    const daysLeft = (new Date(share.expires_at) - new Date()) / (1000 * 60 * 60 * 24);
    if (daysLeft <= 14) return 'expiring';
  }
  return 'active';
}

function formatDate(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const s = Math.round(seconds * 10) / 10;
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return null;
  const { Icon } = cfg;
  return <span className={`sm-st-badge ${cfg.cls}`}><Icon size={13} />{cfg.label}</span>;
}

function TypePill({ type }) {
  return type === 'client'
    ? <span className="sm-type-pill sm-type-client"><LuBriefcase size={12} />Client</span>
    : <span className="sm-type-pill sm-type-album"><LuFolder size={12} />Album</span>;
}

function CopyButton({ url, onToast }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(url).catch(() => {});
    setCopied(true);
    onToast?.('Link copied to clipboard');
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button className={`sm-iconbtn${copied ? ' copied' : ''}`} title="Copy link" onClick={handleCopy}>
      {copied ? <LuCheck size={15} /> : <LuCopy size={15} />}
    </button>
  );
}

function KebabMenu({ share, onAnalytics, onRevoke, onRestore, onDelete, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const item = (Icon, label, fn, cls) => (
    <button className={cls} onClick={(e) => { e.stopPropagation(); setOpen(false); fn?.(); }}>
      <Icon size={15} />{label}
    </button>
  );

  return (
    <div className="sm-kebab" ref={ref}>
      <button className="sm-iconbtn" title="More" onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}>
        <LuEllipsisVertical size={17} />
      </button>
      {open && (
        <div className="sm-menu" onClick={(e) => e.stopPropagation()}>
          {item(LuChartBar, 'View analytics', onAnalytics)}
          {item(LuExternalLink, 'Open share', () => {
            const url = share.shareType === 'album'
              ? `${window.location.origin}/share/${share.token}`
              : share.token ? `${window.location.origin}/g/${share.token}` : null;
            if (url) window.open(url, '_blank');
          })}
          {onNavigate && item(LuFolder, share.shareType === 'album' ? 'Go to album' : 'Go to job', onNavigate)}
          <div className="sm-menu-sep" />
          {share._status === 'revoked'
            ? item(LuRotateCcw, 'Restore share', onRestore)
            : item(LuSlash, 'Revoke access', onRevoke)
          }
          {item(LuTrash2, 'Delete share', onDelete, 'danger')}
        </div>
      )}
    </div>
  );
}

function ShareRow({ share, onToast, onAnalytics, onRevoke, onRestore, onDelete, onNavigate }) {
  const cfg = STATUS_CONFIG[share._status];
  const coverUrl = share.cover_filename ? buildVariantUrl({ filename: share.cover_filename }, 'sm') : null;
  const shareUrl = share.shareType === 'album'
    ? `${window.location.origin}/share/${share.token}`
    : share.token ? `${window.location.origin}/g/${share.token}` : null;

  return (
    <div
      className={`sm-share-row${share._status === 'revoked' ? ' revoked' : ''}`}
      onClick={() => onAnalytics?.(share)}
    >
      <div className={`sm-statusbar ${cfg.barCls}`} />
      {coverUrl && <img className="sm-thumb" src={coverUrl} alt="" loading="lazy" />}
      {!coverUrl && <div className="sm-thumb sm-thumb-placeholder"><LuImage size={20} /></div>}
      <div className="sm-body">
        <div className="sm-r1">
          <span className="sm-title">{share.album_title || share.gallery_title || share.job_title || 'Untitled'}</span>
          <StatusBadge status={share._status} />
          {(share.password || share.has_password || share.password_hash || share.client_password_hash) && (
            <span className="sm-lockchip" title="Password protected"><LuLock size={12} /></span>
          )}
        </div>
        <div className="sm-meta">
          <TypePill type={share.shareType} />
          <span className="sm-m"><LuImage size={13} />{share.photo_count || 0} photos</span>
          <span className="sm-m"><LuEye size={13} /><strong>{share.view_count || 0}</strong> views</span>
          <span className="sm-m"><LuCalendar size={13} />{formatDate(share.created_at)}</span>
          {share.expires_at ? (
            <span className={`sm-m ${share._status === 'expired' ? '' : share._status === 'expiring' ? 'warn' : ''}`}>
              <LuClock size={13} />
              {share._status === 'expired' ? 'Expired ' : 'Expires '}
              {formatDate(share.expires_at)}
            </span>
          ) : (
            <span className="sm-m">No expiry</span>
          )}
        </div>
      </div>
      <div className="sm-actions" onClick={(e) => e.stopPropagation()}>
        {shareUrl && <CopyButton url={shareUrl} onToast={onToast} />}
        <button
          className="sm-iconbtn"
          title="Open in new tab"
          onClick={() => shareUrl && window.open(shareUrl, '_blank')}
        >
          <LuExternalLink size={16} />
        </button>
        <button className="sm-act-btn sm-act-primary" onClick={() => onAnalytics?.(share)}>
          <LuChartBar size={15} />Analytics
        </button>
        <KebabMenu
          share={share}
          onAnalytics={() => onAnalytics?.(share)}
          onRevoke={() => onRevoke?.(share)}
          onRestore={() => onRestore?.(share)}
          onDelete={() => onDelete?.(share)}
          onNavigate={() => onNavigate?.(share)}
        />
      </div>
    </div>
  );
}

function AnalyticsPanel({ share, analytics, loading, onClose, onToast }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    const esc = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', esc);
    return () => { clearTimeout(t); document.removeEventListener('keydown', esc); };
  }, []);

  const handleClose = () => {
    setMounted(false);
    setTimeout(onClose, 300);
  };

  const shareUrl = share.shareType === 'album'
    ? `${window.location.origin}/share/${share.token}`
    : share.token ? `${window.location.origin}/g/${share.token}` : '';
  const shortUrl = shareUrl.replace(/^https?:\/\//, '');

  const maxVisits = analytics?.visitTimeline
    ? Math.max(1, ...analytics.visitTimeline.map(d => d.visits))
    : 1;
  const maxSrc = analytics?.visitorSources
    ? Math.max(1, ...analytics.visitorSources.map(s => s.visitors || 0))
    : 1;

  const weeklyData = useMemo(() => {
    if (!analytics?.visitTimeline?.length) return [];
    const weeks = [];
    const timeline = analytics.visitTimeline.slice(-35);
    for (let i = 0; i < timeline.length; i += 7) {
      const chunk = timeline.slice(i, i + 7);
      const total = chunk.reduce((sum, d) => sum + d.visits, 0);
      const label = new Date(chunk[0].date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      weeks.push({ label, value: total });
    }
    return weeks;
  }, [analytics?.visitTimeline]);

  const maxWeekly = Math.max(1, ...weeklyData.map(w => w.value));

  return (
    <>
      <div className={`sm-overlay${mounted ? ' show' : ''}`} onClick={handleClose} />
      <div className={`sm-ap-panel${mounted ? ' show' : ''}`}>
        <div className="sm-ap-head">
          <button className="sm-iconbtn sm-ap-close" onClick={handleClose} title="Close">
            <LuX size={17} />
          </button>
          <div className="sm-ap-kicker"><LuChartBar size={13} />Share analytics</div>
          <h2>{share.album_title || share.gallery_title || share.job_title || 'Untitled'}</h2>
          {shortUrl && (
            <div className="sm-ap-sublink">
              <code>{shortUrl}</code>
              <CopyButton url={shareUrl} onToast={onToast} />
            </div>
          )}
        </div>
        <div className="sm-ap-body">
          {loading ? (
            <div className="sm-ap-loading"><div className="spinner" /><span>Loading analytics...</span></div>
          ) : analytics?.error ? (
            <div className="sm-ap-upgrade"><LuChartBar size={24} /><p>{analytics.message}</p></div>
          ) : analytics ? (
            <>
              <div className="sm-ap-kpis">
                <div className="sm-ap-kpi hl">
                  <div className="v">{analytics.uniqueVisitors || 0}</div>
                  <div className="l"><LuUsers size={13} />Visitors</div>
                </div>
                <div className="sm-ap-kpi hl">
                  <div className="v">{analytics.totalVisits || 0}</div>
                  <div className="l"><LuEye size={13} />Views</div>
                </div>
                <div className="sm-ap-kpi">
                  <div className="v">{analytics.totalPhotoViews || 0}</div>
                  <div className="l"><LuImage size={13} />Photo views</div>
                </div>
                <div className="sm-ap-kpi">
                  <div className="v">{formatDuration(analytics.avgDuration)}</div>
                  <div className="l"><LuClock size={13} />Avg time</div>
                </div>
              </div>

              {analytics.ownerVisits > 0 && (
                <div className="sm-ap-note">
                  <LuInfo size={15} />
                  <span>Your own visits ({analytics.ownerVisits}) are excluded from these numbers.</span>
                </div>
              )}

              {weeklyData.length > 0 && (
                <div className="sm-ap-sec">
                  <h4>Recent activity</h4>
                  <div className="sm-ap-chart">
                    {weeklyData.map((d, i) => (
                      <div className="col" key={i}>
                        <div
                          className="bar"
                          style={{ height: `${Math.max(6, (d.value / maxWeekly) * 100)}%` }}
                        >
                          {d.value > 0 && <span className="val">{d.value}</span>}
                        </div>
                        <span className="lbl">{d.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analytics.visitorSources?.length > 0 && (
                <div className="sm-ap-sec">
                  <h4>Visitor sources</h4>
                  {analytics.visitorSources.map((src, i) => {
                    const SrcIcon = SOURCE_ICONS[src.source] || LuGlobe;
                    return (
                      <div key={i}>
                        <div className="sm-ap-srcrow">
                          <span className="ic"><SrcIcon size={16} /></span>
                          <span className="nm">{SOURCE_LABELS[src.source] || src.source}</span>
                          <span className="ct">{src.visitors} visitor{src.visitors === 1 ? '' : 's'}</span>
                        </div>
                        <div className="sm-ap-srcbar"><i style={{ width: `${(src.visitors / maxSrc) * 100}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              )}

              {analytics.topPhotos?.length > 0 && (
                <div className="sm-ap-sec">
                  <h4>Most viewed photos</h4>
                  <div className="sm-ap-photos">
                    {analytics.topPhotos.map((photo, i) => (
                      <div className="sm-ap-photo" key={photo.image_id || i}>
                        <img src={buildVariantUrl({ filename: photo.filename }, 'md')} alt={photo.title || ''} loading="lazy" />
                        <span className="v"><LuEye size={12} />{photo.view_count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="sm-ap-sec" style={{ display: 'flex', gap: 8 }}>
                <button
                  className="sm-ap-footbtn"
                  onClick={() => {
                    if (shareUrl) window.open(shareUrl, '_blank');
                  }}
                >
                  <LuExternalLink size={15} />Open share
                </button>
                <button className="sm-ap-footbtn" onClick={() => onToast?.('CSV export coming soon')}>
                  <LuDownload size={15} />Export CSV
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

function EmptyState({ variant, title, body }) {
  const warm = variant === 'warm';
  return (
    <div className={`sm-empty ${warm ? 'warm' : 'blue'}`}>
      <div className="art">
        {warm ? <LuBriefcase size={42} /> : <LuSearch size={42} />}
      </div>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

export default function SharesManager() {
  const navigate = useNavigate();
  const [loadError, setLoadError] = useState(null);
  const loadRequest = useRef({ id: 0, controller: null });
  const [albumShares, setAlbumShares] = useState([]);
  const [clientTokens, setClientTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('recent');
  const [analyticsShare, setAnalyticsShare] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2000);
  }, []);

  useEffect(() => {
    loadAllShares();
    return () => {
      loadRequest.current.id++;
      loadRequest.current.controller?.abort();
    };
  }, []);

  async function loadAllShares() {
    loadRequest.current.controller?.abort();
    const controller = new AbortController();
    const id = ++loadRequest.current.id;
    loadRequest.current.controller = controller;
    setLoading(true);
    setLoadError(null);
    setAlbumShares([]);
    setClientTokens([]);
    try {
      const responses = await Promise.all([
        fetch('/api/shares', { credentials: 'include', signal: controller.signal }),
        fetch('/api/client-tokens', { credentials: 'include', signal: controller.signal }),
      ]);
      const failure = responses.find(response => !response.ok);
      if (failure) {
        const error = new Error('Shares request failed');
        error.status = failure.status;
        throw error;
      }
      const [sharesData, tokensData] = await Promise.all(responses.map(response => response.json()));
      const optional = (value, check) => value == null || check(value);
      const identifier = value => (typeof value === 'string' && value.length > 0) ||
        (typeof value === 'number' && Number.isFinite(value));
      const textFields = ['album_title', 'gallery_title', 'job_title', 'customer_name',
        'cover_filename', 'token', 'password', 'password_hash', 'client_password_hash'];
      const countFields = ['photo_count', 'view_count', 'open_count', 'unique_visitors'];
      const dateFields = ['created_at', 'expires_at', 'revoked_at'];
      const validList = value => Array.isArray(value) && value.every(item =>
        item && typeof item === 'object' && !Array.isArray(item) && identifier(item.id) &&
        ['album_id', 'gallery_id', 'job_id'].every(key => optional(item[key], identifier)) &&
        textFields.every(key => optional(item[key], v => typeof v === 'string')) &&
        countFields.every(key => optional(item[key], v =>
          (typeof v === 'number' && Number.isFinite(v) && v >= 0) ||
          (typeof v === 'string' && /^\d+$/.test(v) && Number.isFinite(Number(v))))) &&
        dateFields.every(key => optional(item[key], v =>
          (typeof v === 'string' || typeof v === 'number') && Number.isFinite(new Date(v).getTime()))) &&
        ['revoked', 'has_password'].every(key => optional(item[key], v =>
          typeof v === 'boolean' || v === 0 || v === 1)));
      if (!validList(sharesData) || !validList(tokensData)) throw new Error('Invalid shares response');
      if (id !== loadRequest.current.id || controller.signal.aborted) return;
      setAlbumShares(sharesData);
      setClientTokens(tokensData);
    } catch (err) {
      if (id !== loadRequest.current.id || controller.signal.aborted) return;
      setLoadError(err.status === 401 ? 'Your session could not be verified. Sign in again to view shares.'
        : [402, 403].includes(err.status) ? 'Shares are unavailable for this account.'
        : 'Could not load shares. Please try again.');
    } finally {
      if (id === loadRequest.current.id && !controller.signal.aborted) setLoading(false);
    }
  }

  const allShares = useMemo(() => {
    const combined = [
      ...albumShares.map(s => ({ ...s, shareType: 'album', _status: getShareStatus(s) })),
      ...clientTokens.map(t => ({ ...t, shareType: 'client', _status: getShareStatus(t) })),
    ];
    return combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [albumShares, clientTokens]);

  const filteredShares = useMemo(() => {
    let pool = activeTab === 'album'
      ? allShares.filter(s => s.shareType === 'album')
      : activeTab === 'client'
        ? allShares.filter(s => s.shareType === 'client')
        : allShares;

    if (statusFilter !== 'all') {
      pool = pool.filter(s => s._status === statusFilter);
    }
    if (query) {
      const q = query.toLowerCase();
      pool = pool.filter(s =>
        (s.album_title || '').toLowerCase().includes(q) ||
        (s.gallery_title || '').toLowerCase().includes(q) ||
        (s.job_title || '').toLowerCase().includes(q) ||
        (s.customer_name || '').toLowerCase().includes(q)
      );
    }

    if (sort === 'views') pool = [...pool].sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    else if (sort === 'name') pool = [...pool].sort((a, b) => (a.album_title || '').localeCompare(b.album_title || ''));

    return pool;
  }, [allShares, activeTab, statusFilter, query, sort]);

  const stats = useMemo(() => ({
    albums: albumShares.length,
    clients: clientTokens.length,
    active: allShares.filter(s => s._status === 'active').length,
  }), [albumShares, clientTokens, allShares]);

  const statusCount = useCallback((k) => {
    const pool = activeTab === 'album'
      ? allShares.filter(s => s.shareType === 'album')
      : activeTab === 'client'
        ? allShares.filter(s => s.shareType === 'client')
        : allShares;
    return k === 'all' ? pool.length : pool.filter(s => s._status === k).length;
  }, [allShares, activeTab]);

  async function openAnalytics(share) {
    setAnalyticsShare(share);
    setAnalyticsData(null);
    setAnalyticsLoading(true);
    if (share.shareType === 'album') {
      try {
        const res = await fetch(`/api/shares/${share.id}/analytics`, { credentials: 'include' });
        setAnalyticsData(await res.json());
      } catch (err) {
        console.error('Failed to load analytics:', err);
        setAnalyticsData({ error: true, message: 'Failed to load analytics' });
      }
    } else {
      setAnalyticsData({
        uniqueVisitors: share.unique_visitors || 0,
        totalVisits: share.view_count || share.open_count || 0,
        totalPhotoViews: 0,
        avgDuration: 0,
        ownerVisits: 0,
      });
    }
    setAnalyticsLoading(false);
  }

  async function revokeShare(share) {
    const endpoint = share.shareType === 'album'
      ? `/api/shares/${share.id}/revoke`
      : `/api/client-tokens/${share.id}/revoke`;
    await fetch(endpoint, { method: 'POST', credentials: 'include' });
    showToast('Share access revoked');
    loadAllShares();
  }

  async function restoreShare(share) {
    if (share.shareType === 'album') {
      await fetch(`/api/shares/${share.id}/reinstate`, { method: 'POST', credentials: 'include' });
      showToast('Share restored');
      loadAllShares();
    }
  }

  async function deleteShare(share) {
    if (!confirm('Delete this share link? This action cannot be undone.')) return;
    const endpoint = share.shareType === 'album' ? `/api/shares/${share.id}` : `/api/client-tokens/${share.id}`;
    await fetch(endpoint, { method: 'DELETE', credentials: 'include' });
    showToast('Share deleted');
    loadAllShares();
  }

  function navigateToSource(share) {
    if (share.shareType === 'album' && share.album_id) navigate(`/albums/${share.album_id}`);
    else if (share.job_id) navigate(`/jobs/${share.job_id}`);
    else if (share.gallery_id) navigate('/clients');
  }

  if (loading || loadError) return (
    <section className="sm-page" aria-busy={loading}>
      <h1 className="sm-title">Shares</h1>
      {loading ? <p role="status">Loading shares...</p> : <>
        <p role="alert">{loadError}</p>
        <button type="button" onClick={loadAllShares}>Try again</button>
        <button type="button" onClick={() => navigate('/photos')}>Back to Photos</button>
      </>}
    </section>
  );

  return (
    <div className="sm-page">
      <div className="sm-wrap">
        {/* Header */}
        <div className="sm-head">
          <div>
            <div className="sm-eyebrow">Sharing</div>
            <h1 className="sm-title">Shares</h1>
            <p className="sm-sub">Manage and monitor every shared album and client gallery in one place.</p>
          </div>
          <div className="sm-stats">
            <div className="sm-stat"><div className="v">{stats.albums}</div><div className="l">Albums</div></div>
            <div className="sm-stat warm"><div className="v">{stats.clients}</div><div className="l">Client</div></div>
            <div className="sm-stat accent"><div className="v">{stats.active}</div><div className="l">Active</div></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="sm-tabs">
          <button className={`sm-tab${activeTab === 'all' ? ' active' : ''}`} onClick={() => setActiveTab('all')}>
            <LuShare2 size={16} />All shares <span className="ct">{allShares.length}</span>
          </button>
          <button className={`sm-tab${activeTab === 'album' ? ' active' : ''}`} onClick={() => setActiveTab('album')}>
            <LuFolder size={16} />Album shares <span className="ct">{albumShares.length}</span>
          </button>
          <button className={`sm-tab warm${activeTab === 'client' ? ' active' : ''}`} onClick={() => setActiveTab('client')}>
            <LuBriefcase size={16} />Client shares <span className="ct">{clientTokens.length}</span>
          </button>
          <span style={{ flex: 1 }} />
          <button className="sm-btn-create" onClick={() => navigate(activeTab === 'client' ? '/clients' : '/albums')}>
            <LuPlus size={15} />Create share
          </button>
        </div>

        {/* Toolbar */}
        <div className="sm-toolbar">
          <div className="sm-search">
            <LuSearch size={16} />
            <input
              placeholder="Search shares, albums, clients..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="sm-filters">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.k}
                className={`sm-chip${statusFilter === f.k ? ' on' : ''}`}
                onClick={() => setStatusFilter(f.k)}
              >
                {f.dot && <span className="dot" style={{ background: f.dot }} />}
                {f.label}
                <span className="ct">{statusCount(f.k)}</span>
              </button>
            ))}
          </div>
          <span className="sm-spacer" />
          <div className="sm-select">
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="recent">Most recent</option>
              <option value="views">Most viewed</option>
              <option value="name">Name A-Z</option>
            </select>
            <LuChevronDown size={14} />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="sm-loading"><div className="spinner" /><span>Loading shares...</span></div>
        ) : filteredShares.length === 0 ? (
          activeTab === 'client' && clientTokens.length === 0 ? (
            <EmptyState
              variant="warm"
              title="No client shares yet"
              body="Client shares deliver galleries to your clients for proofing, selections, and final handoff."
            />
          ) : (
            <EmptyState
              variant="blue"
              title="No shares match"
              body={query ? `Nothing matches "${query}". Try a different search or clear your filters.` : 'No shares with this status. Adjust the filters above to see more.'}
            />
          )
        ) : (
          <div className="sm-list">
            {filteredShares.map(share => (
              <ShareRow
                key={`${share.shareType}-${share.id}`}
                share={share}
                onToast={showToast}
                onAnalytics={openAnalytics}
                onRevoke={revokeShare}
                onRestore={restoreShare}
                onDelete={deleteShare}
                onNavigate={navigateToSource}
              />
            ))}
          </div>
        )}
      </div>

      {/* Analytics panel */}
      {analyticsShare && (
        <AnalyticsPanel
          share={analyticsShare}
          analytics={analyticsData}
          loading={analyticsLoading}
          onClose={() => setAnalyticsShare(null)}
          onToast={showToast}
        />
      )}

      {/* Toast */}
      <div className={`sm-toast${toast ? ' show' : ''}`}><LuCheck size={16} />{toast}</div>
    </div>
  );
}
